import { Controller } from "@hotwired/stimulus"
import { normalizeText, smartMatch } from "../../utils/smart_search"

export default class extends Controller {
  static targets = [
    "container", "grid", "card", "checkbox", "counter", "bottomCounter", 
    "search", "filterButton", "noResults", "progressBar", "progressText",
    "scopeContainer", "scopeRadio", "bulkActions", "globalFiltersContainer",
    "singleClassView", "singleClassCard", "singleClassGrid", "singleClassSearch", "singleClassScrollContainer", "singleClassFilterButton", "singleClassCounter", "singleClassVisibleCount",
    "multipleClassesView", "multipleClassCard", "multipleClassesGrid", "multipleClassesCounter", "multipleClassesVisibleCount", "multipleClassesScrollContainer", "multipleClassesSearch", "multipleClassesFilterButton",
    "completeLevelView", "levelCard", "levelGrid", "levelScrollContainer",
    "multipleLevelsView", "multipleLevelCard", "multipleLevelsGrid", "levelCheckbox", "multipleLevelsScrollContainer",
    "entireSchoolView"
  ]
  
  static values = {
    isNew: Boolean
  }

  connect() {
    console.log("=== CLASSROOM SELECTION CONTROLLER CONNECTED ===")
    this.currentFilter = "all"
    this.searchTerm = ""
    // Don't set a default scope - let user select it
    this.currentScope = null
    this.singleClassCurrentFilter = "all" // Filtre pour Single Class
    this.singleClassSearchTerm = "" // Recherche pour Single Class
    this.multipleClassesCurrentFilter = "all" // Filtre pour Multiple Classes
    this.multipleClassesSearchTerm = "" // Recherche pour Multiple Classes
    
    // Ensure all checkboxes start unchecked on page load
    console.log("Ensuring all checkboxes start unchecked on page load")
    this.ensureAllCheckboxesUnchecked()
    
    // Check if we're creating a new council minute
    const isNewRecord = this.hasIsNewValue ? this.isNewValue : false
    
    // Check if scope is already selected (from server-side form data)
    const selectedScopeRadio = this.hasScopeRadioTarget ? 
      this.scopeRadioTargets.find(radio => radio.checked) : null
    
    if (selectedScopeRadio && !isNewRecord) {
      // Scope is pre-selected from server (editing existing record)
      console.log("Editing existing council minute - scope pre-selected:", selectedScopeRadio.value)
      this.currentScope = selectedScopeRadio.value
      this.showViewForScope(this.currentScope)
      // Load saved selections for this scope
      this.loadSavedSelection()
    } else {
      // No scope selected (new record) - clear localStorage and don't load selections
      if (isNewRecord) {
        console.log("New council minute detected - clearing localStorage")
        localStorage.removeItem('council_minutes_selections')
      } else if (!selectedScopeRadio) {
        console.log("No scope selected - clearing localStorage for new council minute")
        localStorage.removeItem('council_minutes_selections')
      }
      this.hideAllViews()
    }
    
    this.initialize()
    // Don't show default view - wait for user to select a scope
    // this.showDefaultView()
    // Ne pas charger automatiquement les sélections sauvegardées
    // Elles seront chargées seulement si on reste sur le même scope
    
    console.log("=== END CLASSROOM SELECTION CONTROLLER CONNECT ===")
  }
  
  // Ensure all checkboxes are unchecked on initial load
  ensureAllCheckboxesUnchecked() {
    // Check all checkboxes in the page (use querySelectorAll to find all, not just targets)
    const allCheckboxes = document.querySelectorAll('input.classroom-checkbox[type="checkbox"]')
    console.log('Found', allCheckboxes.length, 'checkboxes on page')
    
    let uncheckedCount = 0
    allCheckboxes.forEach(checkbox => {
      if (checkbox.checked) {
        console.log('Found checked checkbox on load - unchecking:', {
          value: checkbox.value,
          name: checkbox.name,
          id: checkbox.id,
          parent: checkbox.closest('.classroom-card')?.dataset.classroomId || 'no parent',
          element: checkbox
        })
        checkbox.checked = false
        uncheckedCount++
      }
    })
    
    if (uncheckedCount > 0) {
      console.log('Unchecked', uncheckedCount, 'checkboxes that were checked on page load')
    } else {
      console.log('All checkboxes were already unchecked')
    }
    
    // Also clear visual state for single class cards
    // Use querySelectorAll since targets might not be initialized yet
    const singleClassCards = document.querySelectorAll('[data-classroom-selection-target="singleClassCard"]')
    console.log('Found', singleClassCards.length, 'single class cards')
    singleClassCards.forEach(card => {
      card.classList.remove('selected')
      const radio = card.querySelector('.w-4.h-4')
      if (radio) {
        radio.classList.remove('border-cta', 'bg-cta')
        radio.classList.add('border-gray-4')
      }
    })
    
    // Verify final state
    const finalChecked = document.querySelectorAll('input.classroom-checkbox[type="checkbox"]:checked')
    console.log('Final checked checkboxes after ensureUnchecked:', finalChecked.length)
    if (finalChecked.length > 0) {
      console.warn('WARNING: Some checkboxes are still checked after ensureUnchecked!')
      finalChecked.forEach(cb => {
        console.warn('  - Still checked:', cb.value, 'name:', cb.name)
      })
    }
  }

  // Mise à jour du compteur de sélection
  updateCounter() {
    let selectedCount = 0
    let totalCount = 0
    
    // Calculer le nombre de classes sélectionnées selon le scope
    switch (this.currentScope) {
      case 'single_class':
        // Pour Single Class, compter les classes sélectionnées dans la vue
        // Count both by visual state and by checkbox state
        const singleClassSelected = this.singleClassCardTargets.filter(card => {
          const cardCheckbox = card.querySelector('input.classroom-checkbox')
          return card.classList.contains('selected') || (cardCheckbox && cardCheckbox.checked)
        }).length
        selectedCount = singleClassSelected
        totalCount = this.singleClassCardTargets.length
        break
        
      case 'multiple_classes':
        // Pour Multiple Classes, compter les checkboxes cochés dans cette vue spécifique
        const multipleClassesCheckboxes = this.multipleClassCardTargets.map(card => 
          card.querySelector('input[type="checkbox"]')
        ).filter(checkbox => checkbox !== null)
        
        selectedCount = multipleClassesCheckboxes.filter(checkbox => checkbox.checked).length
        totalCount = multipleClassesCheckboxes.length
        
        console.log('=== MULTIPLE CLASSES DEBUG ===')
        console.log('Multiple classes checkboxes found:', multipleClassesCheckboxes.length)
        console.log('Selected checkboxes:', selectedCount)
        console.log('Total checkboxes:', totalCount)
        console.log('=== END MULTIPLE CLASSES DEBUG ===')
        break
        
      case 'complete_level':
        // Pour Complete Level, compter les niveaux sélectionnés et leurs classes
        const selectedLevels = this.levelCardTargets.filter(card => 
          card.classList.contains('selected')
        )
        selectedCount = selectedLevels.reduce((total, levelCard) => {
          const cycleCode = levelCard.dataset.cycle
          const classesInLevel = this.cardTargets.filter(card => 
            card.dataset.cycle === cycleCode
          ).length
          return total + classesInLevel
        }, 0)
        totalCount = this.cardTargets.length
        break
        
      case 'multiple_levels':
        // Pour Multiple Levels, compter les niveaux cochés et leurs classes
        const checkedLevels = this.levelCheckboxTargets.filter(checkbox => checkbox.checked)
        selectedCount = checkedLevels.reduce((total, checkbox) => {
          const levelCard = checkbox.closest('[data-classroom-selection-target="multipleLevelCard"]')
          const cycleCode = levelCard.dataset.cycle
          const classesInLevel = this.cardTargets.filter(card => 
            card.dataset.cycle === cycleCode
          ).length
          return total + classesInLevel
        }, 0)
        totalCount = this.cardTargets.length
        break
        
      case 'entire_school':
        // Pour Entire School, toutes les classes sont sélectionnées
        selectedCount = this.cardTargets.length
        totalCount = this.cardTargets.length
        break
        
      default:
        // Vue par défaut (multiple_classes)
        selectedCount = this.checkboxTargets.filter(checkbox => checkbox.checked).length
        totalCount = this.checkboxTargets.length
    }
    
    const percentage = totalCount > 0 ? Math.round((selectedCount / totalCount) * 100) : 0
    
    // Update counter targets only if they exist
    if (this.hasCounterTarget) {
    this.counterTarget.textContent = selectedCount
    }
    if (this.hasBottomCounterTarget) {
    this.bottomCounterTarget.textContent = selectedCount
    }
    
    // Mise à jour de la barre de progression
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }
    
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percentage}%`
    }

    // Cacher l'erreur "aucune classe" si la sélection est > 0
    const errorEl = document.getElementById('classroom-selection-error')
    if (errorEl && selectedCount > 0) {
      errorEl.classList.add('hidden')
    }
    
    // Animation du compteur (only if counter target exists)
    if (this.hasCounterTarget) {
    this.counterTarget.classList.add('counter-animation')
    setTimeout(() => {
        if (this.hasCounterTarget) {
      this.counterTarget.classList.remove('counter-animation')
        }
    }, 300)
    }
    
    // Synchroniser les sélections avec l'état visuel
    this.syncSelections()
    
    // Sauvegarder la sélection
    this.saveSelection()
  }

  // Sélectionner toutes les classes visibles
  selectAll() {
    // Sélectionner selon le scope actuel
    switch (this.currentScope) {
      case 'multiple_levels':
        // Pour Multiple Levels, cocher tous les niveaux visibles
        this.multipleLevelCardTargets.forEach(card => {
          if (card.style.display !== "none") {
            const checkbox = card.querySelector('input[type="checkbox"]')
            if (checkbox && !checkbox.checked) {
              checkbox.checked = true
              checkbox.dispatchEvent(new Event('change'))
            }
          }
        })
        break
        
      case 'multiple_classes':
        // Pour Multiple Classes, cocher toutes les classes visibles
        this.multipleClassCardTargets.forEach(card => {
          if (card.style.display !== "none") {
            const checkbox = card.querySelector('input[type="checkbox"]')
            if (checkbox && !checkbox.checked) {
              checkbox.checked = true
              checkbox.dispatchEvent(new Event('change'))
            }
          }
        })
        break
        
      default:
        // Pour les autres scopes, utiliser la logique par défaut
        this.visibleCards.forEach(card => {
          const checkbox = card.querySelector('input[type="checkbox"]')
          if (checkbox && !checkbox.checked) {
            checkbox.checked = true
            checkbox.dispatchEvent(new Event('change'))
          }
        })
    }
    
    // Mettre à jour le compteur après la sélection
    this.updateCounter()
  }

  // Désélectionner toutes les classes
  deselectAll() {
    // Désélectionner selon le scope actuel
    switch (this.currentScope) {
      case 'multiple_levels':
        // Pour Multiple Levels, décocher tous les niveaux
        this.levelCheckboxTargets.forEach(checkbox => {
          if (checkbox.checked) {
            checkbox.checked = false
            checkbox.dispatchEvent(new Event('change'))
          }
        })
        break
        
      case 'multiple_classes':
        // Pour Multiple Classes, décocher toutes les classes
        this.multipleClassCardTargets.forEach(card => {
          const checkbox = card.querySelector('input[type="checkbox"]')
          if (checkbox && checkbox.checked) {
            checkbox.checked = false
            checkbox.dispatchEvent(new Event('change'))
          }
        })
        break
        
      default:
        // Pour les autres scopes, utiliser la logique par défaut
        this.checkboxTargets.forEach(checkbox => {
          if (checkbox.checked) {
            checkbox.checked = false
            checkbox.dispatchEvent(new Event('change'))
          }
        })
    }
    
    // Mettre à jour le compteur après la désélection
    this.updateCounter()
  }

  // Filtrage par cycle d'enseignement
  filterByCycle(event) {
    const cycle = event.currentTarget.dataset.cycle
    
    // Mise à jour des styles des boutons
    this.filterButtonTargets.forEach(button => {
      if (button.dataset.cycle === cycle) {
        button.classList.remove('bg-gray-4', 'text-black')
        button.classList.add('bg-cta', 'text-white')
      } else {
        button.classList.remove('bg-cta', 'text-white')
        button.classList.add('bg-gray-4', 'text-black')
      }
    })
    
    this.currentFilter = cycle
    
    // Appliquer les filtres selon le scope actuel
    if (this.currentScope === 'multiple_levels') {
      this.applyMultipleLevelsFilters()
    } else {
      this.applyFilters()
    }
  }

  // Filtrage par recherche
  filterClassrooms(event) {
    this.searchTerm = normalizeText(event.target.value)
    
    // Appliquer les filtres selon le scope actuel
    if (this.currentScope === 'multiple_levels') {
      this.applyMultipleLevelsFilters()
    } else {
      this.applyFilters()
    }
  }

  // Filtrage pour la vue Single Class
  filterSingleClass(event) {
    this.singleClassSearchTerm = normalizeText(event.target.value)
    this.applySingleClassFilters()
  }

  // Filtrage par cycle pour Single Class
  filterSingleClassByCycle(event) {
    const cycle = event.currentTarget.dataset.cycle
    
    // Mise à jour des styles des boutons
    this.singleClassFilterButtonTargets.forEach(button => {
      if (button.dataset.cycle === cycle) {
        button.classList.remove('bg-gray-4', 'text-black')
        button.classList.add('bg-blue-500', 'text-white')
      } else {
        button.classList.remove('bg-blue-500', 'text-white')
        button.classList.add('bg-gray-4', 'text-black')
      }
    })
    
    this.singleClassCurrentFilter = cycle
    this.applySingleClassFilters()
  }

  // Application des filtres combinés pour Single Class
  applySingleClassFilters() {
    let visibleCount = 0
    
    this.singleClassCardTargets.forEach(card => {
      const cycle = card.dataset.cycle
      const name = card.dataset.name
      let shouldShow = true
      
      // Filtre par cycle
      if (this.singleClassCurrentFilter !== "all" && cycle !== this.singleClassCurrentFilter) {
        shouldShow = false
      }
      
      // Filtre par recherche
      if (this.singleClassSearchTerm && !smartMatch(name, this.singleClassSearchTerm)) {
        shouldShow = false
      }
      
      // Affichage/masquage avec animation
      if (shouldShow) {
        card.style.display = "block"
        card.classList.remove('opacity-0', 'scale-95')
        card.classList.add('opacity-100', 'scale-100')
        visibleCount++
      } else {
        card.classList.add('opacity-0', 'scale-95')
        setTimeout(() => {
          card.style.display = "none"
        }, 200)
      }
    })
    
    // Mettre à jour le compteur de classes visibles
    this.updateSingleClassCounter()
  }

  // Filtrage pour la vue Multiple Classes
  filterMultipleClasses(event) {
    this.multipleClassesSearchTerm = normalizeText(event.target.value)
    this.applyMultipleClassesFilters()
  }

  // Filtrage par cycle pour Multiple Classes
  filterMultipleClassesByCycle(event) {
    const cycle = event.currentTarget.dataset.cycle
    
    // Mise à jour des styles des boutons
    this.multipleClassesFilterButtonTargets.forEach(button => {
      if (button.dataset.cycle === cycle) {
        button.classList.remove('bg-gray-4', 'text-black')
        button.classList.add('bg-cta', 'text-white')
      } else {
        button.classList.remove('bg-cta', 'text-white')
        button.classList.add('bg-gray-4', 'text-black')
      }
    })
    
    this.multipleClassesCurrentFilter = cycle
    this.applyMultipleClassesFilters()
  }

  // Application des filtres combinés pour Multiple Classes
  applyMultipleClassesFilters() {
    let visibleCount = 0
    
    this.multipleClassCardTargets.forEach(card => {
      const cycle = card.dataset.cycle
      const name = card.dataset.name
      let shouldShow = true
      
      // Filtre par cycle
      if (this.multipleClassesCurrentFilter !== "all" && cycle !== this.multipleClassesCurrentFilter) {
        shouldShow = false
      }
      
      // Filtre par recherche
      if (this.multipleClassesSearchTerm && !smartMatch(name, this.multipleClassesSearchTerm)) {
        shouldShow = false
      }
      
      // Affichage/masquage avec animation
      if (shouldShow) {
        card.style.display = "block"
        card.classList.remove('opacity-0', 'scale-95')
        card.classList.add('opacity-100', 'scale-100')
        visibleCount++
      } else {
        card.classList.add('opacity-0', 'scale-95')
        setTimeout(() => {
          card.style.display = "none"
        }, 200)
      }
    })
    
    // Mettre à jour le compteur de classes visibles
    this.updateMultipleClassesCounter()
  }

  // Mettre à jour le compteur pour Multiple Classes
  updateMultipleClassesCounter() {
    const visibleCount = this.multipleClassCardTargets.filter(card => card.style.display !== "none").length
    const totalCount = this.multipleClassCardTargets.length
    
    // Mettre à jour l'affichage du compteur dans le header
    if (this.hasMultipleClassesVisibleCountTarget) {
      this.multipleClassesVisibleCountTarget.textContent = visibleCount
    }
    
    // Mettre à jour le message complet
    if (this.hasMultipleClassesCounterTarget) {
      if (this.multipleClassesSearchTerm || this.multipleClassesCurrentFilter !== "all") {
        this.multipleClassesCounterTarget.innerHTML = `<span data-classroom-selection-target="multipleClassesVisibleCount">${visibleCount}</span> sur ${totalCount} classe(s)`
      } else {
        this.multipleClassesVisibleCountTarget.textContent = totalCount
      }
    }
  }

  // Mettre à jour le compteur pour Multiple Levels
  updateMultipleLevelsCounter() {
    const visibleCount = this.multipleLevelCardTargets.filter(card => card.style.display !== "none").length
    const totalCount = this.multipleLevelCardTargets.length
    
    // Mettre à jour le compteur dans le header de la vue Multiple Levels
    const counterElement = this.multipleLevelsViewTarget.querySelector('.text-orange-600')
    if (counterElement) {
      if (this.searchTerm || this.currentFilter !== "all") {
        counterElement.textContent = `${visibleCount} sur ${totalCount} niveau(x)`
      } else {
        counterElement.textContent = `${totalCount} niveau(x)`
      }
    }
  }

  // Mettre à jour le compteur pour Single Class
  updateSingleClassCounter() {
    const visibleCount = this.singleClassCardTargets.filter(card => card.style.display !== "none").length
    const totalCount = this.singleClassCardTargets.length
    
    // Mettre à jour l'affichage du compteur dans le header
    if (this.hasSingleClassVisibleCountTarget) {
      this.singleClassVisibleCountTarget.textContent = visibleCount
    }
    
    // Mettre à jour le message complet
    if (this.hasSingleClassCounterTarget) {
      if (this.singleClassSearchTerm || this.singleClassCurrentFilter !== "all") {
        this.singleClassCounterTarget.innerHTML = `<span data-classroom-selection-target="singleClassVisibleCount">${visibleCount}</span> sur ${totalCount} classe(s)`
      } else {
        this.singleClassVisibleCountTarget.textContent = totalCount
      }
    }
  }

  // Application des filtres combinés pour la vue par défaut
  applyFilters() {
    let visibleCount = 0
    
    this.cardTargets.forEach(card => {
      const cycle = card.dataset.cycle
      const name = card.dataset.name
      let shouldShow = true
      
      // Filtre par cycle
      if (this.currentFilter !== "all" && cycle !== this.currentFilter) {
        shouldShow = false
      }
      
      // Filtre par recherche
      if (this.searchTerm && !smartMatch(name, this.searchTerm)) {
        shouldShow = false
      }
      
      // Affichage/masquage avec animation
      if (shouldShow) {
        card.style.display = "block"
        card.classList.remove('opacity-0', 'scale-95')
        card.classList.add('opacity-100', 'scale-100')
        visibleCount++
      } else {
        card.classList.add('opacity-0', 'scale-95')
        setTimeout(() => {
          card.style.display = "none"
        }, 200)
      }
    })
    
    // Affichage du message "aucun résultat"
    if (visibleCount === 0) {
      this.noResultsTarget.classList.remove('hidden')
      this.gridTarget.classList.add('hidden')
    } else {
      this.noResultsTarget.classList.add('hidden')
      this.gridTarget.classList.remove('hidden')
    }
  }

  // Application des filtres combinés pour Multiple Levels
  applyMultipleLevelsFilters() {
    let visibleCount = 0
    
    this.multipleLevelCardTargets.forEach(card => {
      const cycle = card.dataset.cycle
      const name = card.dataset.name
      let shouldShow = true
      
      // Filtre par cycle
      if (this.currentFilter !== "all" && cycle !== this.currentFilter) {
        shouldShow = false
      }
      
      // Filtre par recherche
      if (this.searchTerm && !smartMatch(name, this.searchTerm)) {
        shouldShow = false
      }
      
      // Affichage/masquage avec animation
      if (shouldShow) {
        card.style.display = "block"
        card.classList.remove('opacity-0', 'scale-95')
        card.classList.add('opacity-100', 'scale-100')
        visibleCount++
      } else {
        card.classList.add('opacity-0', 'scale-95')
        setTimeout(() => {
          card.style.display = "none"
        }, 200)
      }
    })
    
    // Mettre à jour le compteur de niveaux visibles
    this.updateMultipleLevelsCounter()
  }

  // Gestion du changement de scope
  onScopeChange(event) {
    const scopeType = event.target.value
    
    console.log('=== SCOPE CHANGE DEBUG ===')
    console.log('Changing from scope:', this.currentScope, 'to:', scopeType)
    
    // Sauvegarder les sélections actuelles avant de changer de scope
    if (this.currentScope !== scopeType) {
      console.log('Saving current selections before scope change')
      this.saveSelection()
    }
    
    this.currentScope = scopeType
    this.showViewForScope(scopeType)
    
    // ALWAYS clear all selections first when switching to single_class
    // This ensures a clean state
    if (scopeType === 'single_class') {
      console.log('Single class scope detected - clearing all selections first')
      this.clearAllSelections()
    }
    
    // Charger les sélections pour le nouveau scope
    console.log('Loading selections for new scope')
    const loaded = this.loadSavedSelection()
    if (!loaded) {
      console.log('No saved selections found, ensuring all are cleared')
      // Si aucune sélection n'a été chargée, effacer toutes les sélections
      this.clearAllSelections()
    } else {
      console.log('Selections loaded successfully')
    }
    
    // Force sync to ensure visual state matches checkbox state
    console.log('Syncing visual state with checkbox state')
    this.syncSelections()
    
    this.updateCounter()
    
    // Log final state
    const checkedBoxes = document.querySelectorAll('input.classroom-checkbox:checked')
    console.log('Final checked checkboxes count:', checkedBoxes.length)
    checkedBoxes.forEach(cb => {
      console.log('  - Checked checkbox value:', cb.value, 'name:', cb.name)
    })
    
    console.log('=== END SCOPE CHANGE DEBUG ===')
  }

  // Afficher la vue selon le scope
  showViewForScope(scopeType) {
    // Masquer toutes les vues
    this.hideAllViews()
    
    // Masquer les actions en masse pour certains scopes (only if target exists)
    if (this.hasBulkActionsTarget) {
    if (scopeType === 'single_class' || scopeType === 'complete_level' || scopeType === 'entire_school') {
      this.bulkActionsTarget.classList.add('hidden')
    } else {
      this.bulkActionsTarget.classList.remove('hidden')
      }
    }
    
    // Masquer les filtres globaux pour les vues qui ont leurs propres filtres (only if target exists)
    if (this.hasGlobalFiltersContainerTarget) {
    if (scopeType === 'single_class' || scopeType === 'multiple_classes' || scopeType === 'complete_level' || scopeType === 'entire_school') {
      this.globalFiltersContainerTarget.classList.add('hidden')
    } else {
      this.globalFiltersContainerTarget.classList.remove('hidden')
      }
    }
    
    // S'assurer que la barre de progression est toujours visible
    if (this.hasProgressBarTarget) {
      const progressContainer = this.progressBarTarget.closest('.bg-gray-50')
      if (progressContainer) {
        progressContainer.classList.remove('hidden')
      }
    }
    
    // Afficher la vue appropriée
    switch (scopeType) {
      case 'single_class':
        if (this.hasSingleClassViewTarget) {
          console.log('Showing single class view')
        this.singleClassViewTarget.classList.remove('hidden')
        this.initializeSingleClassFilters()
          
          // Ensure default grid is hidden and all its checkboxes are unchecked
          if (this.hasGridTarget) {
            this.gridTarget.classList.add('hidden')
            const defaultGridCheckboxes = this.gridTarget.querySelectorAll('input.classroom-checkbox[type="checkbox"]')
            console.log('Unchecking', defaultGridCheckboxes.length, 'checkboxes in default grid')
            defaultGridCheckboxes.forEach(checkbox => {
              if (checkbox.checked) {
                checkbox.checked = false
                console.log('Unchecked default grid checkbox:', checkbox.value)
              }
            })
          }
          
          // Ensure all single class checkboxes start unchecked
          console.log('Initializing single class checkboxes - ensuring all are unchecked')
          if (this.hasSingleClassCardTarget) {
            this.singleClassCardTargets.forEach(card => {
              const checkbox = card.querySelector('input.classroom-checkbox[type="checkbox"]')
              if (checkbox) {
                if (checkbox.checked) {
                  console.log('Found checked checkbox in card:', card.dataset.classroomId, '- unchecking it')
                  checkbox.checked = false
                }
              }
              card.classList.remove('selected')
              const radio = card.querySelector('.w-4.h-4')
              if (radio) {
                radio.classList.remove('border-cta', 'bg-cta')
                radio.classList.add('border-gray-4')
              }
            })
          }
          
        this.updateSingleClassCounter()
        }
        break
      case 'multiple_classes':
        if (this.hasMultipleClassesViewTarget) {
        this.multipleClassesViewTarget.classList.remove('hidden')
        this.initializeMultipleClassesFilters()
        this.updateMultipleClassesCounter()
        }
        break
      case 'complete_level':
        if (this.hasCompleteLevelViewTarget) {
        this.completeLevelViewTarget.classList.remove('hidden')
        }
        break
      case 'multiple_levels':
        if (this.hasMultipleLevelsViewTarget) {
        this.multipleLevelsViewTarget.classList.remove('hidden')
        this.initializeMultipleLevelsFilters()
        this.updateMultipleLevelsCounter()
        }
        break
      case 'entire_school':
        if (this.hasEntireSchoolViewTarget) {
        this.entireSchoolViewTarget.classList.remove('hidden')
        this.selectAllClassrooms()
        }
        break
    }
    
    // Mettre à jour le compteur principal après avoir affiché la vue (only if counter targets exist)
    if (this.hasCounterTarget || this.hasBottomCounterTarget) {
    this.updateCounter()
    }
  }

  // Masquer toutes les vues
  hideAllViews() {
    if (this.hasSingleClassViewTarget) this.singleClassViewTarget.classList.add('hidden')
    if (this.hasMultipleClassesViewTarget) this.multipleClassesViewTarget.classList.add('hidden')
    if (this.hasCompleteLevelViewTarget) this.completeLevelViewTarget.classList.add('hidden')
    if (this.hasMultipleLevelsViewTarget) this.multipleLevelsViewTarget.classList.add('hidden')
    if (this.hasEntireSchoolViewTarget) this.entireSchoolViewTarget.classList.add('hidden')
    if (this.hasGridTarget) {
      this.gridTarget.classList.add('hidden')
      // Also uncheck all checkboxes in the default grid when hiding it
      const defaultGridCheckboxes = this.gridTarget.querySelectorAll('input.classroom-checkbox[type="checkbox"]')
      defaultGridCheckboxes.forEach(checkbox => {
        if (checkbox.checked) {
          checkbox.checked = false
          console.log('Unchecked default grid checkbox when hiding grid:', checkbox.value)
        }
      })
    }
  }

  // Afficher la vue par défaut
  showDefaultView() {
    // Only proceed if scope radio targets exist
    if (!this.hasScopeRadioTarget) {
      console.warn('No scope radio targets found, skipping default view setup')
      return
    }
    
    const selectedScope = this.scopeRadioTargets.find(radio => radio.checked)
    if (selectedScope) {
      this.currentScope = selectedScope.value
      this.showViewForScope(selectedScope.value)
      // Charger les sélections pour le scope par défaut
      this.loadSavedSelection()
    } else {
      // Don't auto-select - wait for user to make a choice
      // Hide all views until user selects a scope
      this.hideAllViews()
    }
    
    // Only update counter if counter targets exist
    if (this.hasCounterTarget || this.hasBottomCounterTarget) {
    this.updateCounter()
    }
  }

  // Sélectionner une seule classe
  selectSingleClass(event) {
    const card = event.currentTarget
    const classroomId = card.dataset.classroomId
    
    console.log('=== SELECT SINGLE CLASS DEBUG ===')
    console.log('Classroom ID:', classroomId)
    console.log('Card:', card)
    
    // Désélectionner toutes les autres cartes
    this.singleClassCardTargets.forEach(c => {
      const radio = c.querySelector('.w-4.h-4')
      if (radio) {
      radio.classList.remove('border-cta', 'bg-cta')
      radio.classList.add('border-gray-4')
      }
      c.classList.remove('selected')
      // Uncheck the checkbox in this card (only checkbox type, not hidden inputs)
      const checkbox = c.querySelector('input.classroom-checkbox[type="checkbox"]')
      if (checkbox) {
        checkbox.checked = false
        console.log('Unchecked checkbox for card:', c.dataset.classroomId)
      }
    })
    
    // Sélectionner la carte cliquée
    const radio = card.querySelector('.w-4.h-4')
    if (radio) {
    radio.classList.remove('border-gray-4')
    radio.classList.add('border-cta', 'bg-cta')
    }
    card.classList.add('selected')
    
    // Check ONLY the checkbox in the clicked card
    // DO NOT check checkboxes in the default grid - they should remain unchecked
    const checkbox = card.querySelector('input.classroom-checkbox[type="checkbox"]')
    
    console.log('Checkbox found:', checkbox)
    
    if (checkbox) {
      checkbox.checked = true
      console.log('Checked checkbox in card, value:', checkbox.value, 'checked:', checkbox.checked, 'name:', checkbox.name)
      // Trigger change event to ensure form submission works
      checkbox.dispatchEvent(new Event('change', { bubbles: true }))
    } else {
      console.error('Checkbox not found in card!')
      // Try to find any checkbox input in the card
      const allInputs = card.querySelectorAll('input')
      console.log('All inputs in card:', allInputs.length)
      allInputs.forEach(input => {
        console.log('  Input type:', input.type, 'name:', input.name, 'value:', input.value, 'class:', input.className)
      })
    }
    
    // IMPORTANT: Do NOT update default grid checkboxes when in single_class mode
    // Only update the checkbox in the clicked card
    // The default grid checkboxes should remain unchecked
    
    // Verify checkbox is checked (only checkboxes in single class view)
    const singleClassChecked = Array.from(this.singleClassCardTargets)
      .map(card => card.querySelector('input.classroom-checkbox[type="checkbox"]'))
      .filter(cb => cb && cb.checked)
    
    console.log('Total checked checkboxes in single class view:', singleClassChecked.length)
    singleClassChecked.forEach(cb => console.log('  - Checkbox value:', cb.value, 'name:', cb.name))
    
    // Also check all checkboxes for debugging (but don't modify them)
    const allChecked = document.querySelectorAll('input.classroom-checkbox:checked')
    console.log('Total checked checkboxes in entire page:', allChecked.length)
    allChecked.forEach((cb, idx) => {
      const parentCard = cb.closest('[data-classroom-selection-target="singleClassCard"]')
      const parentDefaultCard = cb.closest('[data-classroom-selection-target="card"]')
      console.log(`  Checkbox ${idx + 1}:`, {
        value: cb.value,
        name: cb.name,
        id: cb.id,
        inSingleClassView: !!parentCard,
        inDefaultGrid: !!parentDefaultCard,
        parentCardId: parentCard?.dataset.classroomId || parentDefaultCard?.dataset.classroomId || 'none'
      })
    })
    
    this.updateCounter()
    console.log('=== END SELECT SINGLE CLASS DEBUG ===')
  }

  // Sélectionner un niveau complet
  selectCompleteLevel(event) {
    const card = event.currentTarget
    const cycle = card.dataset.cycle
    
    // Désélectionner tous les niveaux
    this.levelCardTargets.forEach(c => {
      c.classList.remove('ring-2', 'ring-purple-500', 'ring-opacity-50', 'selected')
    })
    
    // Sélectionner le niveau cliqué
    card.classList.add('ring-2', 'ring-purple-500', 'ring-opacity-50', 'selected')
    
    // Sélectionner toutes les classes de ce niveau
    this.checkboxTargets.forEach(checkbox => {
      const cardElement = checkbox.closest('.classroom-card')
      if (cardElement && cardElement.dataset.cycle === cycle) {
        checkbox.checked = true
      } else {
        checkbox.checked = false
      }
    })
    
    this.updateCounter()
  }

  // Gestion du changement de niveau multiple
  onLevelChange(event) {
    const levelCheckbox = event.target
    const cycle = levelCheckbox.closest('[data-cycle]').dataset.cycle
    const isChecked = levelCheckbox.checked
    
    // Sélectionner/désélectionner toutes les classes de ce niveau
    this.checkboxTargets.forEach(checkbox => {
      const cardElement = checkbox.closest('.classroom-card')
      if (cardElement && cardElement.dataset.cycle === cycle) {
        checkbox.checked = isChecked
      }
    })
    
    this.updateCounter()
  }

  // Sélectionner toutes les classes (pour entire_school)
  selectAllClassrooms() {
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
    })
    this.updateCounter()
  }

  // Sauvegarder la sélection dans le localStorage
  saveSelection() {
    let selectedIds = []
    
    // Get selected IDs based on current scope
    if (this.currentScope === 'single_class') {
      // For single class, only get checkboxes from single class cards
      selectedIds = Array.from(this.singleClassCardTargets)
        .map(card => card.querySelector('input.classroom-checkbox[type="checkbox"]'))
        .filter(checkbox => checkbox && checkbox.checked)
        .map(checkbox => checkbox.value)
    } else {
      // For other scopes, use checkboxTargets
      selectedIds = this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
    }
    
    // Remove duplicates
    selectedIds = [...new Set(selectedIds)]
    
    // Charger les sélections existantes
    const existingData = this.loadAllSelections()
    
    // Mettre à jour les sélections pour le scope actuel
    existingData[this.currentScope] = {
      selectedIds: selectedIds,
      timestamp: Date.now()
    }
    
    // Sauvegarder toutes les sélections
    localStorage.setItem('council_minutes_selections', JSON.stringify(existingData))
    
    console.log('=== SAVE SELECTION DEBUG ===')
    console.log('Scope:', this.currentScope)
    console.log('Selected IDs (deduplicated):', selectedIds)
    console.log('All saved data:', existingData)
    console.log('=== END SAVE SELECTION DEBUG ===')
  }

  // Charger toutes les sélections depuis le localStorage
  loadAllSelections() {
    const savedSelections = localStorage.getItem('council_minutes_selections')
    if (savedSelections) {
      try {
        return JSON.parse(savedSelections)
      } catch (error) {
        console.warn('Erreur lors du chargement des sélections:', error)
        // Nettoyer les données corrompues
        localStorage.removeItem('council_minutes_selections')
      }
    }
    return {} // Retourner un objet vide si aucune donnée
  }

  // Charger la sélection pour le scope actuel
  loadSavedSelection() {
    const allSelections = this.loadAllSelections()
    const scopeData = allSelections[this.currentScope]
    
      console.log('=== LOAD SELECTION DEBUG ===')
    console.log('Current scope:', this.currentScope)
    console.log('All selections:', allSelections)
    console.log('Scope data:', scopeData)
    
    if (scopeData && scopeData.selectedIds && scopeData.selectedIds.length > 0) {
      console.log('Found saved selections:', scopeData.selectedIds)
      
      // D'abord, décocher toutes les checkboxes
      console.log('Unchecking all checkboxes first')
      this.checkboxTargets.forEach(checkbox => {
        checkbox.checked = false
      })
      
      // Also uncheck single class checkboxes
      if (this.hasSingleClassCardTarget) {
        this.singleClassCardTargets.forEach(card => {
          const checkbox = card.querySelector('input.classroom-checkbox[type="checkbox"]')
          if (checkbox) {
            checkbox.checked = false
          }
        })
      }
      
      // Puis cocher celles qui étaient sauvegardées
      scopeData.selectedIds.forEach(id => {
        console.log('Loading selection for ID:', id)
        
        // Update checkboxes in default grid
        const checkbox = this.checkboxTargets.find(cb => cb.value === id)
        if (checkbox) {
          checkbox.checked = true
          console.log('  ✓ Checked default grid checkbox for ID:', id)
        } else {
          console.log('  ✗ Checkbox not found in default grid for ID:', id)
        }
        
        // Also update checkboxes in single class view if we're in single_class scope
        if (this.currentScope === 'single_class') {
          const singleClassCard = this.singleClassCardTargets.find(card => 
            card.dataset.classroomId === id || card.dataset.classroomId === String(id)
          )
          if (singleClassCard) {
            const cardCheckbox = singleClassCard.querySelector('input.classroom-checkbox[type="checkbox"]')
            if (cardCheckbox) {
              cardCheckbox.checked = true
              console.log('  ✓ Checked single class card checkbox for ID:', id)
            } else {
              console.log('  ✗ Checkbox not found in single class card for ID:', id)
            }
          } else {
            console.log('  ✗ Single class card not found for ID:', id)
          }
        }
      })
      
      // Mettre à jour le compteur après le chargement
      this.updateCounter()
      
      // Synchroniser l'état visuel
      this.syncSelections()
      
      console.log('=== END LOAD SELECTION DEBUG ===')
      return true // Indique que des données ont été chargées
    }
    
    console.log('No saved selections found for scope:', this.currentScope)
    console.log('=== END LOAD SELECTION DEBUG ===')
    return false // Indique qu'aucune donnée n'a été chargée
  }

  // Effacer toutes les sélections
  clearAllSelections() {
    console.log('=== CLEAR ALL SELECTIONS DEBUG ===')
    console.log('Current scope:', this.currentScope)
    
    // Clear default grid checkboxes
    let clearedCount = 0
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
      checkbox.checked = false
        clearedCount++
      }
    })
    console.log('Cleared', clearedCount, 'default grid checkboxes')
    
    // Clear single class checkboxes (always check, not just if in single_class scope)
    let singleClassClearedCount = 0
    if (this.hasSingleClassCardTarget) {
      this.singleClassCardTargets.forEach(card => {
        const checkbox = card.querySelector('input.classroom-checkbox[type="checkbox"]')
        if (checkbox) {
          if (checkbox.checked) {
            checkbox.checked = false
            singleClassClearedCount++
            console.log('Cleared single class checkbox for card:', card.dataset.classroomId)
          }
        }
        card.classList.remove('selected')
        const radio = card.querySelector('.w-4.h-4')
        if (radio) {
          radio.classList.remove('border-cta', 'bg-cta')
          radio.classList.add('border-gray-4')
        }
      })
      console.log('Cleared', singleClassClearedCount, 'single class checkboxes')
    } else {
      console.log('No single class cards found')
    }
    
    // Clear multiple classes checkboxes
    let multipleClassesClearedCount = 0
    if (this.hasMultipleClassCardTarget) {
      this.multipleClassCardTargets.forEach(card => {
        const checkbox = card.querySelector('input.classroom-checkbox[type="checkbox"]')
        if (checkbox && checkbox.checked) {
          checkbox.checked = false
          multipleClassesClearedCount++
        }
        card.classList.remove('selected')
        const indicator = card.querySelector('.selection-indicator')
        if (indicator) {
          indicator.classList.add('hidden')
        }
      })
      console.log('Cleared', multipleClassesClearedCount, 'multiple classes checkboxes')
    }
    
    this.updateCounter()
    
    // Verify all are cleared
    const remainingChecked = document.querySelectorAll('input.classroom-checkbox:checked')
    console.log('Remaining checked checkboxes after clear:', remainingChecked.length)
    if (remainingChecked.length > 0) {
      console.warn('WARNING: Some checkboxes are still checked!')
      remainingChecked.forEach(cb => {
        console.warn('  - Still checked:', cb.value, 'name:', cb.name, 'element:', cb)
      })
    }
    
    console.log('=== END CLEAR ALL SELECTIONS DEBUG ===')
  }

  // Nettoyer le localStorage
  clearLocalStorage() {
    localStorage.removeItem('council_minutes_selections')
    this.clearAllSelections()
    // Afficher un message de confirmation
    this.showNotification('Données sauvegardées nettoyées', 'success')
  }

  // Synchroniser les sélections avec l'état visuel
  syncSelections() {
    console.log('=== SYNC SELECTIONS DEBUG ===')
    console.log('Syncing for scope:', this.currentScope)
    
    // Mettre à jour les indicateurs visuels selon le scope actuel
    switch (this.currentScope) {
      case 'multiple_classes':
        this.checkboxTargets.forEach(checkbox => {
          const card = checkbox.closest('.classroom-card')
          if (card) {
            const indicator = card.querySelector('.selection-indicator')
            if (indicator) {
              if (checkbox.checked) {
                indicator.classList.remove('hidden')
                card.classList.add('selected')
              } else {
                indicator.classList.add('hidden')
                card.classList.remove('selected')
              }
            }
          }
        })
        break
        
      case 'single_class':
        // Pour Single Class, mettre à jour les indicateurs de sélection
        this.singleClassCardTargets.forEach(card => {
          const classroomId = card.dataset.classroomId
          // Check both the checkbox in the card and the default grid checkboxes
          const cardCheckbox = card.querySelector('input.classroom-checkbox[type="checkbox"]')
          const isSelected = (cardCheckbox && cardCheckbox.checked) || 
                           this.checkboxTargets.some(checkbox => 
            checkbox.value === classroomId && checkbox.checked
          )
          
          const radio = card.querySelector('.w-4.h-4')
          if (radio) {
            if (isSelected) {
              radio.classList.remove('border-gray-4')
              radio.classList.add('border-cta', 'bg-cta')
              card.classList.add('selected')
              // Ensure checkbox is checked
              if (cardCheckbox) {
                cardCheckbox.checked = true
              }
            } else {
              radio.classList.remove('border-cta', 'bg-cta')
              radio.classList.add('border-gray-4')
              card.classList.remove('selected')
              // Ensure checkbox is unchecked
              if (cardCheckbox) {
                cardCheckbox.checked = false
              }
            }
          }
        })
        break
        
      case 'complete_level':
        // Pour Complete Level, mettre à jour les indicateurs de niveau
        this.levelCardTargets.forEach(card => {
          const cycle = card.dataset.cycle
          const isSelected = this.checkboxTargets.some(checkbox => {
            const cardElement = checkbox.closest('.classroom-card')
            return cardElement && cardElement.dataset.cycle === cycle && checkbox.checked
          })
          
          if (isSelected) {
            card.classList.add('ring-2', 'ring-purple-500', 'ring-opacity-50', 'selected')
          } else {
            card.classList.remove('ring-2', 'ring-purple-500', 'ring-opacity-50', 'selected')
          }
        })
        break
        
      case 'multiple_levels':
        // Pour Multiple Levels, mettre à jour les checkboxes de niveau
        this.levelCheckboxTargets.forEach(checkbox => {
          const levelCard = checkbox.closest('[data-cycle]')
          const cycle = levelCard.dataset.cycle
          const allClassesInLevel = this.checkboxTargets.filter(cb => {
            const cardElement = cb.closest('.classroom-card')
            return cardElement && cardElement.dataset.cycle === cycle
          })
          const selectedClassesInLevel = allClassesInLevel.filter(cb => cb.checked)
          
          // Cocher le niveau si toutes ses classes sont sélectionnées
          checkbox.checked = selectedClassesInLevel.length === allClassesInLevel.length && allClassesInLevel.length > 0
        })
        break
    }
    
    console.log('=== END SYNC SELECTIONS DEBUG ===')
  }

  // Afficher une notification
  showNotification(message, type = 'info') {
    // Créer une notification temporaire
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 px-4 py-2 rounded shadow-lg z-50 ${
      type === 'success' ? 'bg-cta text-white' : 
      type === 'error' ? 'bg-red-500 text-white' : 
      'bg-blue-500 text-white'
    }`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    // Supprimer après 3 secondes
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 3000)
  }

  // Effacer la recherche
  clearSearch() {
    this.searchTarget.value = ""
    this.searchTerm = ""
    this.applyFilters()
  }

  // Getter pour les cartes visibles
  get visibleCards() {
    return this.cardTargets.filter(card => card.style.display !== "none")
  }

  // Raccourcis clavier
  initialize() {
    document.addEventListener('keydown', (event) => {
      // Ctrl+A pour tout sélectionner
      if (event.ctrlKey && event.key === 'a') {
        event.preventDefault()
        this.selectAll()
      }
      
      // Ctrl+D pour tout désélectionner
      if (event.ctrlKey && event.key === 'd') {
        event.preventDefault()
        this.deselectAll()
      }
      
      // Échap pour effacer la recherche
      if (event.key === 'Escape') {
        this.clearSearch()
      }
      
      // Ctrl+R pour forcer la synchronisation
      if (event.ctrlKey && event.key === 'r') {
        event.preventDefault()
        this.forceSync()
      }
    })
  }

  // Forcer la synchronisation
  forceSync() {
    this.syncSelections()
    this.updateCounter()
    this.showNotification('Synchronisation forcée effectuée', 'success')
  }

  // Initialiser les filtres pour Single Class
  initializeSingleClassFilters() {
    // Réinitialiser les filtres
    this.singleClassCurrentFilter = "all"
    this.singleClassSearchTerm = ""
    
    // Réinitialiser la barre de recherche
    if (this.hasSingleClassSearchTarget) {
      this.singleClassSearchTarget.value = ""
    }
    
    // Réinitialiser les boutons de filtre
    this.singleClassFilterButtonTargets.forEach(button => {
      if (button.dataset.cycle === "all") {
        button.classList.remove('bg-gray-4', 'text-black')
        button.classList.add('bg-blue-500', 'text-white')
      } else {
        button.classList.remove('bg-blue-500', 'text-white')
        button.classList.add('bg-gray-4', 'text-black')
      }
    })
    
    // Afficher toutes les cartes
    this.singleClassCardTargets.forEach(card => {
      card.style.display = "block"
      card.classList.remove('opacity-0', 'scale-95')
      card.classList.add('opacity-100', 'scale-100')
    })
  }

  // Initialiser les filtres pour Multiple Classes
  initializeMultipleClassesFilters() {
    // Réinitialiser les filtres
    this.multipleClassesCurrentFilter = "all"
    this.multipleClassesSearchTerm = ""
    
    // Réinitialiser la barre de recherche
    if (this.hasMultipleClassesSearchTarget) {
      this.multipleClassesSearchTarget.value = ""
    }
    
    // Réinitialiser les boutons de filtre
    this.multipleClassesFilterButtonTargets.forEach(button => {
      if (button.dataset.cycle === "all") {
        button.classList.remove('bg-gray-4', 'text-black')
        button.classList.add('bg-cta', 'text-white')
      } else {
        button.classList.remove('bg-cta', 'text-white')
        button.classList.add('bg-gray-4', 'text-black')
      }
    })
    
    // Afficher toutes les cartes
    this.multipleClassCardTargets.forEach(card => {
      card.style.display = "block"
      card.classList.remove('opacity-0', 'scale-95')
      card.classList.add('opacity-100', 'scale-100')
    })
    
    // S'assurer que les checkboxes ont les bons listeners
    this.multipleClassCardTargets.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]')
      if (checkbox) {
        // Supprimer les anciens listeners pour éviter les doublons
        checkbox.removeEventListener('change', this.updateCounter.bind(this))
        // Ajouter le nouveau listener
        checkbox.addEventListener('change', this.updateCounter.bind(this))
      }
    })
    
    // Mettre à jour le compteur après l'initialisation
    this.updateCounter()
  }

  // Initialiser les filtres pour Multiple Levels
  initializeMultipleLevelsFilters() {
    // Réinitialiser les filtres globaux
    this.currentFilter = "all"
    this.searchTerm = ""
    
    // Réinitialiser la barre de recherche globale
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
    }
    
    // Réinitialiser les boutons de filtre globaux
    this.filterButtonTargets.forEach(button => {
      if (button.dataset.cycle === "all") {
        button.classList.remove('bg-gray-4', 'text-black')
        button.classList.add('bg-cta', 'text-white')
      } else {
        button.classList.remove('bg-cta', 'text-white')
        button.classList.add('bg-gray-4', 'text-black')
      }
    })
    
    // Afficher toutes les cartes de niveau
    this.multipleLevelCardTargets.forEach(card => {
      card.style.display = "block"
      card.classList.remove('opacity-0', 'scale-95')
      card.classList.add('opacity-100', 'scale-100')
    })
  }
} 